// ═══════════════════════════════════════════════════════════════════════════════
// Pair invite session (helper opens employer /pair-code/:code link)
// ═══════════════════════════════════════════════════════════════════════════════

import { ApiClientError } from './api';

const PENDING_PAIR_INVITE_KEY = 'fdh.pendingPairInvite';

export type PendingPairInvite = {
  code: string;
  employerNickname: string;
};

export const writePendingPairInvite = (data: PendingPairInvite): void => {
  sessionStorage.setItem(
    PENDING_PAIR_INVITE_KEY,
    JSON.stringify({
      code: data.code.trim().toUpperCase(),
      employerNickname: data.employerNickname,
    })
  );
};

export const readPendingPairInvite = (): PendingPairInvite | null => {
  const raw = sessionStorage.getItem(PENDING_PAIR_INVITE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PendingPairInvite;
    if (!parsed.code || !parsed.employerNickname) {
      return null;
    }
    return {
      code: parsed.code.trim().toUpperCase(),
      employerNickname: parsed.employerNickname,
    };
  } catch {
    return null;
  }
};

export const clearPendingPairInvite = (): void => {
  sessionStorage.removeItem(PENDING_PAIR_INVITE_KEY);
};

export const hasPendingPairInvite = (): boolean => readPendingPairInvite() !== null;

export const buildPairInviteUrl = (code: string): string =>
  `${window.location.origin}/pair-code/${code.trim().toUpperCase()}`;

/** Join failures that should show the “ask employer for a new link” page. */
export const isPairInviteJoinExpiredError = (err: unknown): boolean => {
  if (!(err instanceof ApiClientError)) {
    return false;
  }
  if (err.code === 'PAIRING_CODE_CONTACT_EMPLOYER') {
    return true;
  }
  if (err.status === 400) {
    return true;
  }
  if (err.status === 409 && err.code !== 'HELPER_ALREADY_PAIRED') {
    return true;
  }
  return false;
};

export const isHelperAlreadyPairedJoinError = (err: unknown): boolean =>
  err instanceof ApiClientError && err.code === 'HELPER_ALREADY_PAIRED';
